import { useNotification } from "@context/NotificationContext";
import {
  claimResumeCall,
  claimVesselCall,
  unclaimResumeCall,
  unclaimVesselCall,
} from "@services/vesselCallApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { t } from "i18next";

export interface UseClaimVesselCallProps {
  onClaimVesselCall: () => void;
}

export interface UseUnclaimVesselCallProps {
  onUnclaimVesselCall: () => void;
}

export interface UseClaimResumeCallProps {
  onClaimResumeCall: () => void;
}

export interface UseUnclaimResumeCallProps {
  onUnclaimResumeCall: () => void;
}
const service = "bff-vessel-call";

export const useClaimVesselCall = (
  vesselCallId: string | undefined,
  refetchVesselCall: () => void,
): UseClaimVesselCallProps => {
  const { notify } = useNotification();

  const claimMutation = useMutation({
    mutationFn: async () => {
      if (!vesselCallId) return;
      return runTransaction(
        {
          module: "vessel_call",
          screen: "VesselCallStatus",
          action: "claim_vessel_call",
          entityType: "vessel_call",
        },
        (transactionId) =>
          claimVesselCall({
            service,
            vesselCallId: vesselCallId,
            ...(transactionId ? { transactionId } : {}),
          }),
      );
    },
    onSuccess: async () => {
      notify({
        title: t("vessel-call.vessel-call-claimed"),
        type: "success",
      });
      await refetchVesselCall();
    },
    onError: (error: Error) => {
      notify({
        title: t("common.error"),
        description: error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const onClaimVesselCall = () => claimMutation.mutate();

  return { onClaimVesselCall };
};

export const useClaimResumeCall = (
  vesselCallId: string | undefined,
  refetchVesselCall: () => void,
  refetchInterruption: () => Promise<unknown>,
): UseClaimResumeCallProps => {
  const { notify } = useNotification();

  const claimMutation = useMutation({
    mutationFn: async () => {
      if (!vesselCallId) return;
      return runTransaction(
        {
          module: "vessel_call",
          screen: "VesselCallResumption",
          action: "claim_vessel_call_resumption",
          entityType: "vessel_call",
        },
        (transactionId) =>
          claimResumeCall({
            service,
            vesselCallId: vesselCallId,
            ...(transactionId ? { transactionId } : {}),
          }),
      );
    },
    onSuccess: async () => {
      notify({
        title: t("vessel-call.vessel-call-claimed"),
        type: "success",
      });
      await refetchVesselCall();
      await refetchInterruption();
    },
    onError: (error: Error) => {
      notify({
        title: t("common.error"),
        description: error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const onClaimResumeCall = () => claimMutation.mutate();

  return { onClaimResumeCall };
};

export const useUnclaimVesselCall = (
  vesselCallId: string | undefined,
  refetchVesselCall: () => void,
): UseUnclaimVesselCallProps => {
  const { notify } = useNotification();

  const unclaimMutation = useMutation({
    mutationFn: async () => {
      if (!vesselCallId) return;
      return runTransaction(
        {
          module: "vessel_call",
          screen: "VesselCallStatus",
          action: "unclaim_vessel_call",
          entityType: "vessel_call",
        },
        (transactionId) =>
          unclaimVesselCall({
            service,
            vesselCallId: vesselCallId,
            ...(transactionId ? { transactionId } : {}),
          }),
      );
    },
    onSuccess: async () => {
      notify({
        title: t("vessel-call.vessel-call-unclaimed"),
        type: "success",
      });
      await refetchVesselCall();
    },
    onError: (error: Error) => {
      notify({
        title: t("common.error"),
        description: error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const onUnclaimVesselCall = () => unclaimMutation.mutate();

  return { onUnclaimVesselCall };
};

export const useUnclaimResumeCall = (
  vesselCallId: string | undefined,
  refetchVesselCall: () => void,
  refetchInterruption: () => Promise<unknown>,
): UseUnclaimResumeCallProps => {
  const { notify } = useNotification();

  const unclaimMutation = useMutation({
    mutationFn: async () => {
      if (!vesselCallId) return;
      return runTransaction(
        {
          module: "vessel_call",
          screen: "VesselCallResumption",
          action: "unclaim_vessel_call_resumption",
          entityType: "vessel_call",
        },
        (transactionId) =>
          unclaimResumeCall({
            service,
            vesselCallId: vesselCallId,
            ...(transactionId ? { transactionId } : {}),
          }),
      );
    },
    onSuccess: async () => {
      notify({
        title: t("vessel-call.vessel-call-unclaimed"),
        type: "success",
      });
      await refetchVesselCall();
      await refetchInterruption();
    },
    onError: (error: Error) => {
      notify({
        title: t("common.error"),
        description: error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const onUnclaimResumeCall = () => unclaimMutation.mutate();

  return { onUnclaimResumeCall };
};
